import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper, Stack, Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar.jsx';
import skyshift from '../../Images/dt_logo.png';
import salesforceImg from '../../workflows/images/salesforce.png';
import oracleImg from '../../workflows/images/oracle.png';
import hubspotImg from '../../workflows/images/hubspot.png';
import { selectConnectionsCount } from '../../state/slices/connectionsSlice';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../components/ui/button';

const IconCard = ({ src, alt, label }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: { xs: 0.5, sm: 0.75, md: 1 },
      position: 'relative',
      zIndex: 20,
    }}
  >
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        width: { xs: 32, sm: 36, md: 40 },
        height: { xs: 32, sm: 36, md: 40 },
        objectFit: 'contain',
        border: '1px solid',
        borderColor: 'rgba(90, 102, 166, 0.2)',
        borderRadius: 1,
        p: { xs: 0.25, sm: 0.375, md: 0.5 },
        position: 'relative',
        zIndex: 21,
        backgroundColor: '#ffffff',
      }}
    />
    <Typography
      variant="caption"
      sx={{
        fontWeight: 600,
        color: '#2C3E50',
        letterSpacing: '0.01em',
        textAlign: 'center',
        fontSize: { xs: '0.625rem', sm: '0.6875rem', md: '0.75rem' },
      }}
    >
      {label}
    </Typography>
  </Box>
);

IconCard.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  label: PropTypes.string.isRequired,
};

IconCard.defaultProps = {
  alt: 'Connector Icon',
};

const Connector = () => {
  const navigate = useNavigate();

  const connectionsCount = useSelector(selectConnectionsCount);

  const handleCreateConnection = () => {
    navigate('/create-connections');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#F5F7FA' }}>
      <Sidebar />
      <Box sx={{ flex: 1, p: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ maxWidth: { xs: '100%', sm: 800, md: 900 }, mx: 'auto' }}>
          <Box sx={{ mb: { xs: 3, sm: 4, md: 5 }, textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: 'IBM Plex Sans, sans-serif',
                fontWeight: 700,
                fontStyle: 'normal',
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                lineHeight: 1.5,
                letterSpacing: '0%',
                textAlign: 'center',
                color: '#3B9564',
                mb: { xs: 1, sm: 1.25, md: 1.5 },
              }}
            >
              Connect Your Data Sources
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                lineHeight: 1.5,
                letterSpacing: '0%',
                textAlign: 'center',
                color: '#383838',
                maxWidth: { xs: '100%', sm: 500, md: 600 },
                mx: 'auto',
                px: { xs: 1, sm: 0 },
              }}
            >
              Link multiple sources to destinations using scalable data connectors.
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              p: { xs: 2, sm: 3, md: 5 },
              borderRadius: '4px',
              bgcolor: '#ffffff',
              border: '1px solid',
              borderColor: 'rgba(90, 102, 166, 0.08)',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
              overflow: 'visible',
              gap: { xs: 3, md: 0 },
            }}
          >
            <Box
              component="svg"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 0,
                display: { xs: 'none', md: 'block' },
              }}
              viewBox="0 0 1000 400"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient
                  id="lineGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#3B9564" stopOpacity="0.6" />
                  <stop offset="50%" stopColor="#3B9564" stopOpacity="1" />
                  <stop offset="100%" stopColor="#3B9564" stopOpacity="0.6" />
                </linearGradient>
                <linearGradient
                  id="flowGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#3B9564" stopOpacity="0.3" />
                  <stop offset="40%" stopColor="#3B9564" stopOpacity="0.3" />
                  <stop offset="50%" stopColor="#3B9564" stopOpacity="1" />
                  <stop offset="60%" stopColor="#3B9564" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#3B9564" stopOpacity="0.3" />
                </linearGradient>
                <linearGradient
                  id="oracleLineGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#3B9564" stopOpacity="0.3" />
                  <stop offset="20%" stopColor="#3B9564" stopOpacity="0.9" />
                  <stop offset="80%" stopColor="#3B9564" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#3B9564" stopOpacity="0.3" />
                </linearGradient>
              </defs>

              <path
                d="M 170 110 L 315 110 C 320 110, 320 195, 325 200 L 450 200"
                stroke="#3B9564"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.3"
              />
              <path
                d="M 170 110 L 315 110 C 320 110, 320 195, 325 200 L 450 200"
                stroke="url(#flowGradient)"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="20 200"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="220"
                  to="0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>

              <path
                d="M 170 200 L 450 200"
                stroke="#3B9564"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                opacity="0.2"
              />
              <path
                d="M 170 200 L 450 200"
                stroke="url(#flowGradient)"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="20 200"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="250"
                  to="0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>

              <path
                d="M 170 310 L 315 310 C 320 310, 320 205, 325 200 L 450 200"
                stroke="#3B9564"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.2"
              />
              <path
                d="M 170 310 L 315 310 C 320 310, 320 205, 325 200 L 450 200"
                stroke="url(#flowGradient)"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="20 200"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="220"
                  to="0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>

              <path
                d="M 550 200 L 665 200 C 670 200, 670 115, 675 110 L 830 110"
                stroke="#3B9564"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.2"
              />
              <path
                d="M 550 200 L 665 200 C 670 200, 670 115, 675 110 L 830 110"
                stroke="url(#flowGradient)"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="20 200"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="220"
                  to="0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>

              <path
                d="M 550 200 L 830 200"
                stroke="#3B9564"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                opacity="0.2"
              />
              <path
                d="M 550 200 L 830 200"
                stroke="url(#flowGradient)"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="20 200"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="250"
                  to="0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>

              <path
                d="M 550 200 L 665 200 C 670 200, 670 305, 675 310 L 830 310"
                stroke="#3B9564"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.2"
              />
              <path
                d="M 550 200 L 665 200 C 670 200, 670 305, 675 310 L 830 310"
                stroke="url(#flowGradient)"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="20 200"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="220"
                  to="0"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>
            </Box>

            <Stack
              spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
              sx={{ zIndex: 20, minWidth: { xs: '100%', sm: 160, md: 180 }, position: 'relative', width: { xs: '100%', md: 'auto' } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: { xs: 0.25, md: 0.5 } }}>
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                    lineHeight: { xs: '1.125rem', md: '1.25rem' },
                    letterSpacing: { xs: '0.4px', md: '0.55px' },
                    textTransform: 'uppercase',
                    color: '#6A7282',
                    textAlign: 'center',
                  }}
                >
                  Sources
                </Typography>
                <Tooltip
                  title={
                    <Typography
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.85rem' },
                        lineHeight: 1.5,
                        fontFamily: 'IBM Plex Sans, sans-serif',
                        color: '#FFFFFF',
                      }}
                    >
                      Data sources are where your data originates from. Connect to various systems like Salesforce, Oracle databases, and HubSpot to extract and import data into your ETL workflows.
                    </Typography>
                  }
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: { xs: 250, sm: 280, md: 300 },
                        p: { xs: 1, sm: 1.25, md: 1.5 },
                        borderRadius: '8px',
                        backgroundColor: '#1A2332',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        '& .MuiTooltip-arrow': {
                          color: '#1A2332',
                        },
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      padding: 0.25,
                      color: '#64748B',
                      '&:hover': {
                        color: '#3B9564',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <IconCard
                src={salesforceImg}
                alt="Salesforce"
                label="Salesforce"
              />
              <IconCard src={oracleImg} alt="Oracle" label="Oracle DB" />
              <IconCard src={hubspotImg} alt="HubSpot" label="HubSpot" />
            </Stack>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                zIndex: 20,
                px: { xs: 2, sm: 3, md: 4 },
                position: 'relative',
                my: { xs: 2, md: 0 },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: { xs: 60, sm: 75, md: 90 },
                  height: { xs: 60, sm: 75, md: 90 },
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#F8F9FA',
                  boxShadow: '0 8px 24px rgba(90, 102, 166, 0.15)',
                  position: 'relative',
                  p: 0,
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={skyshift}
                  alt="Skyshift"
                  sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0 }}
                />
              </Paper>
            </Box>

            <Stack
              spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
              sx={{ zIndex: 20, minWidth: { xs: '100%', sm: 160, md: 180 }, position: 'relative', width: { xs: '100%', md: 'auto' } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: { xs: 0.25, md: 0.5 } }}>
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                    lineHeight: { xs: '1.125rem', md: '1.25rem' },
                    letterSpacing: { xs: '0.4px', md: '0.55px' },
                    textTransform: 'uppercase',
                    color: '#6A7282',
                    textAlign: 'center',
                  }}
                >
                  Destinations
                </Typography>
                <Tooltip
                  title={
                    <Typography
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.85rem' },
                        lineHeight: 1.5,
                        fontFamily: 'IBM Plex Sans, sans-serif',
                        color: '#FFFFFF',
                      }}
                    >
                      Destinations are where your transformed data is sent. Export processed data to systems like Salesforce, Oracle databases, and HubSpot to complete your data pipeline.
                    </Typography>
                  }
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: { xs: 250, sm: 280, md: 300 },
                        p: { xs: 1, sm: 1.25, md: 1.5 },
                        borderRadius: '8px',
                        backgroundColor: '#1A2332',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        '& .MuiTooltip-arrow': {
                          color: '#1A2332',
                        },
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      padding: 0.25,
                      color: '#64748B',
                      '&:hover': {
                        color: '#3B9564',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <IconCard
                src={salesforceImg}
                alt="Salesforce"
                label="Salesforce"
              />
              <IconCard src={oracleImg} alt="Oracle" label="Oracle DB" />
              <IconCard src={hubspotImg} alt="HubSpot" label="HubSpot" />
            </Stack>
          </Paper>

          <Box sx={{ textAlign: 'center', mt: { xs: 2, sm: 3, md: 3 } }}>
            <PrimaryButton
              variant="contained"
              size="large"
              onClick={handleCreateConnection}
              sx={{
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1, sm: 1.25, md: 1.5 },
                fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {connectionsCount > 0
                ? 'Create a New Connection'
                : 'Create Your First Connection'}
            </PrimaryButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Connector;